import { getAuth } from 'firebase/auth';
import { IRecord } from 'interfaces';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import Head from 'components/elements/Head';
import Loading from 'components/elements/Loading';
import Meta from 'components/elements/Meta';
import { useAuthStore } from 'modules/auth/store/authStore';
import { authStoreActions } from 'modules/auth/store/authStore/actions';
import { SharedReadingExperience } from 'modules/listening/components/SharedReadingExperience';
import { readerStoreActions } from 'modules/listening/features/reader/stores/readerStore';
import { listeningModeActions } from 'modules/listening/stores/listeningMode/actions';
import { ListeningOwnership } from 'modules/listening/stores/listeningMode/listeningModeStore';
import { ListenableContent, SDKFacade, getSDK } from 'modules/sdk/lib';
import { logSegmentEvent } from 'utils/analytics';
import { useSessionAuth } from 'utils/withAuth';
import withLegacyBaseApp from 'wrappers/withLegacyBaseApp';

const LoadingSkeleton = () => (
  <div className="flex items-center justify-center p-24">
    <Loading />
  </div>
);

const ListeningExperience = dynamic(
  () =>
    /* webpackChunkName: "modules/listening/components/ReaderBasedListeningExperience" */
    import('modules/listening/components/ReaderBasedListeningExperience').then(mod => mod.ReaderBasedListeningExperience),
  {
    ssr: false,
    loading: () => <LoadingSkeleton />
  }
);

const SharePage = () => {
  const router = useRouter();
  const itemId = router.query.id as string;
  const [item, setItem] = useState<IRecord | undefined>();
  const [listenableContent, setListenableContent] = useState<ListenableContent>();
  const currentUser = useAuthStore(state => state.user);
  const isCurrentUserAvailable = currentUser !== null;

  const onUserLoaded = useCallback(() => {
    const currentFirebaseUser = getAuth().currentUser;
    if (!currentFirebaseUser) {
      authStoreActions.loginAnonymously();
    }
  }, []);

  useSessionAuth(onUserLoaded, onUserLoaded);

  useEffect(() => {
    const createListenableContent = async (sdk: SDKFacade) => {
      const { content, library } = sdk;

      const sdkItem = await library.getItem(itemId);
      const item = library.libraryItemToIRecord(sdkItem);

      setItem(item);
      if (item?.recordType) {
        logSegmentEvent('web_app_shared_document_opened', { type: item.recordType.toString().toLowerCase() });
      }
      const listenableContent = content.createListenableContent(sdkItem);
      readerStoreActions.determineReaderMode(listenableContent);
      listeningModeActions.setListeningModeOwnership(ListeningOwnership.SHARED);
      setListenableContent(listenableContent);
    };
    const initialize = async () => {
      const sdk = await getSDK();
      createListenableContent(sdk);
    };

    if (isCurrentUserAvailable) {
      initialize();
    }
  }, [currentUser?.isAnonymous, isCurrentUserAvailable, itemId, router]);

  const renderExperience = useMemo(() => {
    if (listenableContent) {
      return <ListeningExperience listenableContent={listenableContent} />;
    }
    return <LoadingSkeleton />;
  }, [listenableContent]);

  if (!item) return null;

  return (
    <Fragment>
      <Head title={item.title} />
      <Meta />

      <div className="min-h-screen h-screen">
        <SharedReadingExperience item={item}>{renderExperience}</SharedReadingExperience>
      </div>
    </Fragment>
  );
};

export default withLegacyBaseApp(SharePage);
